import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';

const sections = [
  {
    title: '1. Purpose',
    paragraphs: [
      'FILM-HOOK MEDIAAPPS PRIVATE LIMITED ("Filmhook Apps") respects every user\'s right to control their personal information and account. This Account Deletion Policy explains how users may permanently delete their Filmhook Apps account, what information is removed, what information may be retained, and the legal and operational reasons for retaining certain data.',
      'This Policy should be read together with our Privacy Policy, Data Safety & Security Policy, and Terms of Service.'
    ]
  },
  {
    title: '2. Eligibility for Account Deletion',
    paragraphs: [
      'Registered users may request the permanent deletion of their Filmhook Apps account at any time through the account settings within the application or by contacting our official support team.',
      'Before processing a deletion request, Filmhook Apps may verify the identity of the account holder to protect against unauthorized deletion requests.'
    ]
  },
  {
    title: '3. User-Initiated Account Deletion',
    paragraphs: [
      'Users may permanently delete their account by using the Delete Account option available within the Filmhook Apps application or through other official deletion methods provided by Filmhook Apps.',
      'Once a deletion request is submitted, users may lose access to:'
    ],
    bullets: [
      'User profile',
      'Professional profile',
      'Business profile',
      'Portfolio information',
      'Uploaded photos and videos',
      'Marketplace listings',
      'Recruitment activities',
      'Saved preferences',
      'Connections and followers',
      'Messages (subject to applicable retention requirements)',
      'Other account-related information'
    ],
    footerParagraph: 'Account deletion is intended to permanently remove the user\'s access to Filmhook Apps.'
  },
  {
    title: '4. Information Deleted',
    paragraphs: [
      'Subject to applicable laws and legitimate business requirements, Filmhook Apps will make reasonable efforts to delete or anonymize information associated with the deleted account, including:'
    ],
    bullets: [
      'Account profile information',
      'Public profile details',
      'Portfolio information',
      'Uploaded media',
      'Marketplace listings'
    ]
  }
];

const AccountDeletion = () => {
  return (
    <Box sx={{ py: { xs: 4, sm: 8, md: 12 }, minHeight: '80vh' }}>
      <Container maxWidth="md">
        <Box
          sx={{
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid var(--border-color)',
            borderRadius: '24px',
            p: { xs: 2.5, sm: 4, md: 7 },
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          }}
        >
          <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: 'var(--text-heading)', textAlign: 'center', mb: 1, fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' } }}>
            Account Deletion Policy
          </Typography>
          <Typography variant="h6" sx={{ color: '#5dade2', textAlign: 'center', mb: 3, fontWeight: 500, letterSpacing: '0.5px', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' } }}>
            FILM-HOOK MEDIAAPPS PRIVATE LIMITED
          </Typography>
          <Box sx={{ width: 80, height: 3, background: 'linear-gradient(90deg, #07a4f5, #5dade2)', mx: 'auto', mb: 6, borderRadius: '2px' }} />

          {sections.map((section, idx) => (
            <Box key={idx} sx={{ mb: 6 }}>
              <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: 'var(--text-heading)', mb: 2, fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' } }}>
                {section.title}
              </Typography>
              
              {section.paragraphs.map((p, pIdx) => (
                <Typography key={pIdx} variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mb: 2, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                  {p}
                </Typography>
              ))}

              {section.bullets && (
                <List sx={{ pl: 2, mb: 2 }}>
                  {section.bullets.map((bullet, bIdx) => (
                    <ListItem key={bIdx} alignItems="flex-start" disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: '32px', mt: '8px' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5dade2' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                            {bullet}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {section.footerParagraph && (
                <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mt: 2, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                  {section.footerParagraph}
                </Typography>
              )}

              {idx < sections.length - 1 && (
                <Divider sx={{ mt: 5, borderColor: 'var(--border-subtle)' }} />
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default AccountDeletion;
